// StocksHero.jsx — "Troféu 52 Semanas" pinned hero for the Stocks page.
// Crowns the index with the best 52-week return (the leading exchange), shows
// flag/level/52w%, and uses the dedicated news-search service as its header.
// Self-contained dark card (no viewport assumptions). HeroPinned-inspired.
//
// Reuses verified patterns from AssetPage:
//   - historical : GET :8080/api/yahoo/v2/{ticker}  -> { candles:[{ts,open,high,low,close,volume}] }
//   - news search: GET :5020/api/news/ticker/{symbol} -> { news:[...] } (202 = still building, retry)
//
// BACKLOG: HERO_IMG is a generic corporate-finance CDN wildcard. Swap for the
// SwarmUI-generated thumbnail bank (per-exchange/per-country) when that ships.

import React, { useState, useEffect } from 'react';

// Index universe ranked for the 52-week trophy. Self-contained on purpose.
const INDEX_SYMBOLS = [
  '^N225', '^HSI', '^AXJO', '000001.SS', '^KS11', '^TWII', '^JKSE', '^STI', '^KLSE',
  '^FTSE', '^GDAXI', '^FCHI', '^STOXX50E',
  '^GSPC', '^DJI', '^IXIC', '^BVSP',
  'BTC-USD',
];

const API_URL  = process.env.REACT_APP_API_URL  || '/api/go';
const NEWS_API = process.env.REACT_APP_NEWS_API  || '/api/news82';

const HERO_IMG = 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1600&q=80';

// ── Index metadata (kept local so the hero is self-contained) ──
const NAMES_MAP = {
  '^N225': 'Nikkei 225', '^HSI': 'Hang Seng', '^AXJO': 'S&P/ASX 200',
  '000001.SS': 'SSE Composite', '^FTSE': 'FTSE 100', '^GDAXI': 'DAX',
  '^FCHI': 'CAC 40', '^STOXX50E': 'EURO STOXX 50', '^GSPC': 'S&P 500',
  '^DJI': 'Dow Jones', '^IXIC': 'NASDAQ', '^BVSP': 'IBOVESPA',
  'BTC-USD': 'Bitcoin', '^KS11': 'KOSPI', '^TWII': 'TWSE Composite',
  '^JKSE': 'IDX Composite', '^STI': 'STI Index', '^KLSE': 'Bursa Malaysia',
};
const ISO_MAP = {
  '^N225': 'jp', '^HSI': 'hk', '^AXJO': 'au', '000001.SS': 'cn', '^FTSE': 'gb',
  '^GDAXI': 'de', '^FCHI': 'fr', '^STOXX50E': 'eu', '^GSPC': 'us', '^DJI': 'us',
  '^IXIC': 'us', '^BVSP': 'br', '^KS11': 'kr', '^TWII': 'tw', '^JKSE': 'id',
  '^STI': 'sg', '^KLSE': 'my', 'BTC-USD': 'BITCOIN',
};
const COUNTRY_MAP = {
  '^N225': 'Japão', '^HSI': 'Hong Kong', '^AXJO': 'Austrália', '000001.SS': 'China',
  '^FTSE': 'Reino Unido', '^GDAXI': 'Alemanha', '^FCHI': 'França', '^STOXX50E': 'Zona do Euro',
  '^GSPC': 'EUA', '^DJI': 'EUA', '^IXIC': 'EUA', '^BVSP': 'Brasil', '^KS11': 'Coreia do Sul',
  '^TWII': 'Taiwan', '^JKSE': 'Indonésia', '^STI': 'Singapura', '^KLSE': 'Malásia', 'BTC-USD': 'Cripto',
};

// ── Historical fetch (replicated from AssetPage) ──
async function fetchHistory(ticker) {
  try {
    const res = await fetch(`${API_URL}/api/yahoo/v2/${ticker}`);
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.candles || !data.candles.length) return null;
    return data.candles
      .map(c => ({ ts: c.ts, close: c.close, high: c.high, low: c.low }))
      .filter(c => c.close != null);
  } catch { return null; }
}

// Compute 52-week stats from a candle series
function stats52w(candles) {
  if (!candles || candles.length < 2) return null;
  const oneYearAgo = Math.floor(Date.now() / 1000) - 365 * 86400;
  const within = candles.filter(c => c.ts >= oneYearAgo);
  const s = within.length >= 2 ? within : candles;
  const first = s[0].close, last = s[s.length - 1].close;
  if (!first || !last) return null;
  return {
    ret: (last / first - 1) * 100,
    level: last,
    high: Math.max(...s.map(c => c.high ?? c.close)),
    low:  Math.min(...s.map(c => c.low  ?? c.close)),
  };
}

const fmtNum = (n) => n == null ? '—'
  : Number(n).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function flagSrc(sym) {
  const code = ISO_MAP[sym];
  if (!code || code === 'BITCOIN') return null;
  return `https://flagcdn.com/w160/${code}.png`;
}

// ── News header strip (news-search service, AssetPage retry pattern) ──
function NewsHeader({ symbol }) {
  const [news, setNews]     = useState([]);
  const [status, setStatus] = useState('loading'); // loading | fetching | ready | error

  useEffect(() => {
    if (!symbol) return;
    let alive = true;
    const run = async (retry = 0) => {
      try {
        const r = await fetch(`${NEWS_API}/api/news/ticker/${symbol}`);
        if (r.status === 202) {           // still building the search index
          if (!alive) return;
          setStatus('fetching');
          if (retry < 3) setTimeout(() => run(retry + 1), 5000);
          return;
        }
        if (r.ok && alive) {
          const d = await r.json();
          setNews(d.news || []);
          setStatus('ready');
        }
      } catch { if (alive) setStatus('error'); }
    };
    setStatus('loading'); setNews([]);
    run();
    return () => { alive = false; };
  }, [symbol]);

  const label =
    status === 'ready' && news.length ? `${news.length} MANCHETES` :
    status === 'fetching' ? 'BUSCANDO…' :
    status === 'error' ? 'INDISPONÍVEL' : 'CARREGANDO…';

  const top = news.slice(0, 4);

  return (
    <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 14, marginTop: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 9, fontWeight: 700,
          letterSpacing: '0.14em', color: '#6660fe', border: '1px solid #6660fe44',
          padding: '2px 7px', borderRadius: 2 }}>GEOPOLÍTICA · NER</span>
        <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 9, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.1em' }}>
          {label}
        </span>
      </div>

      {top.length > 0 ? (
        <div style={{ display: 'grid', gap: 8 }}>
          {top.map((n, i) => (
            <a key={n.id || n.url || n.link || i}
              href={n.url || n.link || '#'} target="_blank" rel="noopener noreferrer"
              style={{ textDecoration: 'none', display: 'flex', gap: 8, alignItems: 'baseline' }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
              <span style={{ color: '#6660fe', fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, flexShrink: 0 }}>
                {String(i + 1).padStart(2, '0')}
              </span>
              <span style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontSize: 12.5,
                fontWeight: 400, color: 'rgba(255,255,255,0.82)', lineHeight: 1.35,
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {n.title || n.headline || '—'}
                {n.source && <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, marginLeft: 6 }}>· {n.source}</span>}
              </span>
            </a>
          ))}
        </div>
      ) : (
        <div style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>
          {status === 'error'
            ? 'Serviço de notícias indisponível para este índice.'
            : 'Buscando cobertura para o índice líder…'}
        </div>
      )}
    </div>
  );
}

// ── Main hero ──
export function StocksHero({ theme }) {
  const [ranked, setRanked]   = useState(null);   // [{symbol, ret, level, ...}] sorted desc
  const [imgOk, setImgOk]     = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      const results = await Promise.allSettled(
        INDEX_SYMBOLS.map(async (sym) => {
          const c = await fetchHistory(sym);
          const s = stats52w(c);
          return s ? { symbol: sym, ...s } : null;
        })
      );
      if (!alive) return;
      const rows = results
        .filter(r => r.status === 'fulfilled' && r.value)
        .map(r => r.value)
        .sort((a, b) => b.ret - a.ret);
      setRanked(rows);
    })();
    return () => { alive = false; };
  }, []);

  const winner  = ranked && ranked.length ? ranked[0] : null;
  const podium  = ranked ? ranked.slice(1, 4) : [];
  const loading = ranked === null;

  return (
    <div style={{
      position: 'relative', overflow: 'hidden',
      background: theme.cardBg, border: `1px solid ${theme.border}`,
      borderRadius: 16, marginBottom: '2rem',
    }}>
      {/* backdrop: gradient (always) + CDN image overlay (graceful) */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 0,
        background: 'linear-gradient(115deg, rgba(102,96,254,0.18), rgba(8,8,8,0.0) 55%), radial-gradient(120% 120% at 90% 10%, rgba(102,96,254,0.12), transparent 60%)' }} />
      {imgOk && (
        <img src={HERO_IMG} alt="" onError={() => setImgOk(false)}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: 'cover', opacity: 0.12, zIndex: 0, filter: 'grayscale(0.4)' }} />
      )}

      <div style={{ position: 'relative', zIndex: 1, padding: '1.75rem 2rem' }}>
        {/* trophy badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 700,
            letterSpacing: '0.16em', color: '#000', background: 'linear-gradient(90deg,#FCD34D,#D4AF37)',
            padding: '4px 12px', borderRadius: 100 }}>
            🏆 TROFÉU · 52 SEMANAS
          </span>
          <span style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontSize: 12, color: theme.textSecondary }}>
            A bolsa que está vencendo a batalha global
          </span>
        </div>

        {loading ? (
          <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 11, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.12em', padding: '1.5rem 0' }}>
            APURANDO O LÍDER DE 52 SEMANAS…
          </div>
        ) : !winner ? (
          <div style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontSize: 13, color: theme.textSecondary, padding: '1rem 0' }}>
            Sem histórico disponível para os índices — verifique o proxy <code>:8080/api/yahoo/v2</code>.
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
              {flagSrc(winner.symbol) && (
                <img src={flagSrc(winner.symbol)} alt=""
                  style={{ width: 64, height: 'auto', borderRadius: 4, boxShadow: '0 4px 16px rgba(0,0,0,0.5)' }} />
              )}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 11, color: theme.textSecondary, letterSpacing: '0.1em' }}>
                  {COUNTRY_MAP[winner.symbol] || ''} · {winner.symbol}
                </div>
                <div style={{ fontFamily: "'Roble',system-ui,sans-serif", fontWeight: 800,
                  fontSize: 'clamp(1.8rem,4vw,2.6rem)', color: '#fff', lineHeight: 1.05, letterSpacing: '-0.02em' }}>
                  {NAMES_MAP[winner.symbol] || winner.symbol}
                </div>
              </div>
              <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                <div style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontWeight: 600,
                  fontSize: 'clamp(1.6rem,4vw,2.4rem)', color: winner.ret >= 0 ? '#4ade80' : '#f87171',
                  fontVariantNumeric: 'tabular-nums' }}>
                  {winner.ret >= 0 ? '+' : ''}{winner.ret.toFixed(1)}%
                </div>
                <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, color: theme.textSecondary, letterSpacing: '0.08em' }}>
                  52 SEM · NÍVEL {fmtNum(winner.level)}
                </div>
              </div>
            </div>

            {/* podium: runners-up */}
            {podium.length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
                {podium.map((p, i) => (
                  <div key={p.symbol} style={{ display: 'flex', alignItems: 'center', gap: 7,
                    background: 'rgba(255,255,255,0.05)', border: `1px solid ${theme.border}`,
                    borderRadius: 8, padding: '5px 10px' }}>
                    <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, color: theme.textSecondary }}>{i + 2}º</span>
                    {flagSrc(p.symbol) && <img src={flagSrc(p.symbol)} alt="" style={{ width: 18, borderRadius: 2 }} />}
                    <span style={{ fontFamily: "'Roble',system-ui,sans-serif", fontWeight: 800, fontSize: 11, color: '#fff' }}>
                      {NAMES_MAP[p.symbol] || p.symbol}
                    </span>
                    <span style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontWeight: 600, fontSize: 11,
                      color: p.ret >= 0 ? '#4ade80' : '#f87171', fontVariantNumeric: 'tabular-nums' }}>
                      {p.ret >= 0 ? '+' : ''}{p.ret.toFixed(1)}%
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* news-search header for the winner */}
            <NewsHeader symbol={winner.symbol} />
          </>
        )}
      </div>
    </div>
  );
}

export default StocksHero;